import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Injections1 from '../assets/Injections1.png'
import infusion1 from '../assets/infusion1.png'
import oncology1 from '../assets/image3.png'

const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const items = [
  {
    id: 1,
    img: oncology1,
    titleKey: 'products.oncologyTitle',
    descKey:  'products.oncologyDesc',
    href: '/products/oncology',
    num: '01',
  },
  {
    id: 2,
    img: Injections1,
    titleKey: 'products.injectionsTitle',
    descKey:  'products.injectionsDesc',
    href: '/products/injections',
    num: '02',
  },
  {
    id: 3,
    img: infusion1,
    titleKey: 'products.infusionsTitle',
    descKey:  'products.infusionsDesc',
    href: '/products/infusions',
    num: '03',
  },
];

const ProductRow = ({ item, index, t }) => {
  const [ref, vis] = useReveal(0);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`pr-row ${vis ? 'in' : ''} ${flip ? 'flipped' : ''}`}
    >
      {/* Image side */}
      <div className="pr-row-img-wrap">
        <img src={item.img} alt={t(item.titleKey)} className="pr-row-img" />
        <div className="pr-row-img-overlay" />
        {/* number watermark on image */}
        <div className="pr-row-num">{item.num}</div>
      </div>

      {/* Content side */}
      <div className="pr-row-content">
        <div className="pr-row-eyebrow">{t("productsSection.eyebrow")}</div>
        <h3 className="pr-row-title">{t(item.titleKey)}</h3>
        <div className="pr-row-divider" />
        <p className="pr-row-desc">{t(item.descKey)}</p>
        <Link to={item.href} className="pr-row-btn">
          {t("common.explore") || 'Explore'}
          <span className="pr-row-btn-arrow">→</span>
        </Link>
      </div>
    </div>
  );
};

const Products = () => {
  const [headerRef, headerVis] = useReveal(0);
  const { t } = useTranslation();

  return (
    <section className="pr-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes prRowL { from{opacity:0;transform:translateX(-52px)} to{opacity:1;transform:translateX(0)} }
        @keyframes prRowR { from{opacity:0;transform:translateX(52px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes prHead { from{opacity:0;transform:translateY(36px)}  to{opacity:1;transform:translateY(0)} }

        .pr-root {
          font-family: 'Outfit', sans-serif;
          background: linear-gradient(180deg, #f0f6ff 0%, #fff 50%, #f8faff 100%);
          position: relative; overflow: hidden;
          padding: 100px 0 80px;
        }

        /* Subtle dot texture */
        .pr-dots {
          position: absolute; inset: 0; pointer-events: none;
          background-image: radial-gradient(circle, rgba(58,143,163,0.05) 1px, transparent 1px);
          background-size: 38px 38px;
        }

        .pr-inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; position: relative; z-index: 1; }

        /* ── HEADER ── */
        .pr-header {
          text-align: center; margin-bottom: 72px;
          opacity: 0; transform: translateY(36px);
          transition: all 0.8s cubic-bezier(0.4,0,0.2,1);
        }
        .pr-header.in { opacity: 1; transform: translateY(0); }

        .pr-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #3a8fa3; background: rgba(58,143,163,0.08);
          border: 1px solid rgba(58,143,163,0.22);
          padding: 5px 16px; border-radius: 999px; margin-bottom: 16px;
        }
        .pr-section-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 3rem); font-weight: 800; color: #1a3a4a; margin: 0 0 14px;
        }
        .pr-subtitle { font-size: 1rem; color: #64748b; max-width: 560px; margin: 0 auto; line-height: 1.7; }

        /* ── ROWS: stacked alternating ── */
        .pr-rows { display: flex; flex-direction: column; gap: 28px; }

        .pr-row {
          display: grid; grid-template-columns: 1fr 1fr;
          border-radius: 24px; overflow: hidden;
          box-shadow: 0 8px 40px rgba(58,143,163,0.10), 0 2px 8px rgba(0,0,0,0.05);
          border: 1px solid rgba(58,143,163,0.08);
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          opacity: 0; transform: translateX(-52px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s, border-color 0.3s;
          min-height: 320px;
        }
        @media(max-width:768px){ .pr-row{grid-template-columns:1fr;} }

        .pr-row.in { opacity: 1; transform: translateX(0); }
        .pr-row.flipped { transform: translateX(52px); }
        .pr-row.flipped.in { transform: translateX(0); }

        .pr-row:hover {
          box-shadow: 0 20px 56px rgba(58,143,163,0.16), 0 6px 18px rgba(0,0,0,0.08);
          border-color: rgba(58,143,163,0.2);
        }

        /* flipped: image goes to right column */
        .pr-row.flipped .pr-row-img-wrap { order: 2; }
        .pr-row.flipped .pr-row-content  { order: 1; }
        @media(max-width:768px){
          .pr-row.flipped .pr-row-img-wrap { order: 0; }
          .pr-row.flipped .pr-row-content  { order: 0; }
        }

        /* Image side */
        .pr-row-img-wrap {
          position: relative; overflow: hidden;
        }
        .pr-row-img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.65s cubic-bezier(0.4,0,0.2,1);
        }
        .pr-row:hover .pr-row-img { transform: scale(1.06); }
        .pr-row-img-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(13,34,51,0.18) 0%, transparent 55%);
        }

        /* Big watermark number on image */
        .pr-row-num {
          position: absolute; bottom: 12px; right: 16px;
          font-family: 'Playfair Display', serif; font-size: 4.5rem; font-weight: 800;
          color: rgba(255,255,255,0.12); line-height: 1;
          pointer-events: none; user-select: none;
        }

        /* Content side */
        .pr-row-content {
          padding: 48px 48px;
          display: flex; flex-direction: column; justify-content: center;
        }
        @media(max-width:768px){ .pr-row-content { padding: 36px 28px; } }

        .pr-row-eyebrow {
          font-size: 0.72rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase;
          color: #3a8fa3; margin-bottom: 12px;
        }
        .pr-row-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.5rem, 2.5vw, 2.1rem); font-weight: 800;
          color: #1a3a4a; margin: 0 0 16px; line-height: 1.2;
          transition: color 0.3s;
        }
        .pr-row:hover .pr-row-title { color: #3a8fa3; }
        .pr-row-divider {
          width: 36px; height: 3px; border-radius: 2px; margin-bottom: 16px;
          background: linear-gradient(90deg, #3a8fa3, #5aab9a);
        }
        .pr-row-desc {
          font-size: 0.93rem; color: #64748b; line-height: 1.75; margin-bottom: 28px;
        }
        .pr-row-btn {
          display: inline-flex; align-items: center; gap: 9px;
          font-size: 0.85rem; font-weight: 600; color: #fff;
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          padding: 11px 24px; border-radius: 999px; border: none; cursor: pointer;
          box-shadow: 0 6px 20px rgba(58,143,163,0.3);
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
          text-decoration: none; align-self: flex-start;
        }
        .pr-row-btn:hover {
          background: linear-gradient(135deg, #2d7a8e, #4a9888);
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(58,143,163,0.42);
        }
        .pr-row-btn-arrow { transition: transform 0.3s; }
        .pr-row-btn:hover .pr-row-btn-arrow { transform: translateX(4px); }
      `}</style>

      <div className="pr-dots" />
      <div className="pr-inner">
        <div ref={headerRef} className={`pr-header ${headerVis ? 'in' : ''}`}>
          <div className="pr-eyebrow">{t("productsSection.eyebrow")}</div>
          <h2 className="pr-section-title">{t("productsSection.title")}</h2>
          <p className="pr-subtitle">{t("productsSection.subtitle")}</p>
        </div>
        <div className="pr-rows">
          {items.map((item, i) => (
            <ProductRow key={item.id} item={item} index={i} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;